/**
 * Tracks WDIO commands as timeline steps.
 *
 * Uses beforeCommand/afterCommand hooks to measure duration
 * and categorize each W3C command.
 */

import type { AppiumCommandStep, CommandCategory } from './types.js';
import { redactArgs } from './redaction.js';

const FIND_COMMANDS = new Set([
  'findElement', 'findElements', 'findElementFromElement',
  'findElementsFromElement', 'findElementFromShadowRoot',
  'findElementsFromShadowRoot', '$', '$$',
]);

const INTERACTION_COMMANDS = new Set([
  'elementClick', 'elementClear', 'elementSendKeys',
  'setValueImmediate', 'replaceValue', 'click', 'clearValue',
  'setValue', 'addValue',
]);

const GESTURE_COMMANDS = new Set([
  'touchAction', 'multiAction', 'performActions', 'releaseActions',
  'touchPerform', 'multiTouchPerform',
]);

const NAVIGATION_COMMANDS = new Set([
  'navigateTo', 'back', 'forward', 'refresh', 'getUrl', 'getTitle',
]);

const LIFECYCLE_COMMANDS = new Set([
  'installApp', 'removeApp', 'terminateApp', 'activateApp',
  'queryAppState', 'isAppInstalled',
]);

const CONTEXT_COMMANDS = new Set([
  'switchContext', 'getContext', 'getContexts',
]);

const DEVICE_COMMANDS = new Set([
  'setOrientation', 'getOrientation', 'setGeoLocation',
  'getGeoLocation', 'getNetworkConnection', 'setNetworkConnection',
]);

const KEYBOARD_COMMANDS = new Set([
  'hideKeyboard', 'isKeyboardShown', 'pressKeyCode', 'longPressKeyCode',
]);

const ALERT_COMMANDS = new Set([
  'getAlertText', 'acceptAlert', 'dismissAlert', 'sendAlertText',
]);

const SCREENSHOT_COMMANDS = new Set([
  'takeScreenshot', 'takeElementScreenshot', 'saveScreenshot',
]);

export function categorizeCommand(commandName: string): CommandCategory {
  if (FIND_COMMANDS.has(commandName)) return 'find';
  if (INTERACTION_COMMANDS.has(commandName)) return 'interaction';
  if (GESTURE_COMMANDS.has(commandName)) return 'gesture';
  if (NAVIGATION_COMMANDS.has(commandName)) return 'navigation';
  if (LIFECYCLE_COMMANDS.has(commandName)) return 'lifecycle';
  if (CONTEXT_COMMANDS.has(commandName)) return 'context';
  if (DEVICE_COMMANDS.has(commandName)) return 'device';
  if (KEYBOARD_COMMANDS.has(commandName)) return 'keyboard';
  if (ALERT_COMMANDS.has(commandName)) return 'alert';
  if (SCREENSHOT_COMMANDS.has(commandName)) return 'screenshot';

  if (commandName.startsWith('mobile:') || commandName.startsWith('mobile ')) {
    const sub = commandName.replace(/^mobile[:\s]+/, '').toLowerCase();
    if (sub.includes('scroll') || sub.includes('swipe') || sub.includes('fling') || sub.includes('drag')) {
      return 'gesture';
    }
    if (sub.includes('deeplink') || sub.includes('navigate')) return 'navigation';
    if (sub.includes('install') || sub.includes('activate') || sub.includes('terminate')) return 'lifecycle';
    if (sub.includes('lock') || sub.includes('unlock') || sub.includes('shake')) return 'device';
    if (sub.includes('hide') && sub.includes('keyboard')) return 'keyboard';
    if (sub.includes('screenshot') || sub.includes('record')) return 'screenshot';
  }

  return 'other';
}

function extractSelector(args: unknown[]): string | undefined {
  if (args.length === 0) return undefined;
  const first = args[0];
  if (typeof first === 'string') return first;
  if (typeof first === 'object' && first !== null) {
    const obj = first as Record<string, unknown>;
    if (typeof obj.value === 'string') return obj.value;
    if (typeof obj.selector === 'string') return obj.selector;
  }
  return undefined;
}

interface PendingCommand {
  commandName: string;
  args: unknown[];
  startTime: number;
}

export class CommandTracker {
  private readonly steps: AppiumCommandStep[] = [];
  private pending: PendingCommand | null = null;
  private enabled: boolean;

  constructor(enabled: boolean) {
    this.enabled = enabled;
  }

  beforeCommand(commandName: string, args: unknown[]): void {
    if (!this.enabled) return;
    this.pending = {
      commandName,
      args,
      startTime: Date.now(),
    };
  }

  afterCommand(commandName: string, _args: unknown[], _result: unknown, error?: Error): void {
    if (!this.enabled || !this.pending) return;
    if (this.pending.commandName !== commandName) return;

    const duration = Date.now() - this.pending.startTime;
    const category = categorizeCommand(commandName);
    const safeArgs = redactArgs(this.pending.args, commandName);

    const step: AppiumCommandStep = {
      type: 'command',
      command: commandName,
      category,
      args: safeArgs.length > 0 ? safeArgs : undefined,
      selector: extractSelector(this.pending.args),
      timestamp: new Date(this.pending.startTime).toISOString(),
      duration,
      result: error ? 'error' : 'success',
      error: error?.message,
    };

    this.steps.push(step);
    this.pending = null;
  }

  getSteps(): AppiumCommandStep[] {
    return [...this.steps];
  }

  clear(): void {
    this.steps.length = 0;
    this.pending = null;
  }

  dispose(): void {
    this.clear();
  }
}
